import { useEffect, useMemo, useState } from 'react'
import clsx from 'clsx';
import Loader from '../../../components/Loader';
import ActionsPopOnMe from '../../../components/ActionsPopOnMe';
import CreateVerification from './CreateVerification';
import useModal from '../../../hooks/modalHook';
import { exportVehicleVerificationPhotos, getVerifiedVehicleById, reloadVehicleVerificationPhotos, requestVehicleVerificationPhotos } from '../../../api/requests';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { VerifiedVehicle } from '../../../models/verifiedVehicle/VerifiedVehicle';
import { EllipsisVerticalIcon, PencilIcon } from '@heroicons/react/24/outline';
import moment from 'moment';
import { formatCurrencyAmount, getFormattedDate, getPhoneNumberFromShortCodeFormat, showToast } from '../../../utils/miscFunctions';
import useUserAccount from '../../../hooks/userAccountHook';
import useRoute from '../../../hooks/routesHook';
import ActivityStepper from '../../../components/ActivityStepper';
import { LuCalendarClock } from 'react-icons/lu';
import { BsCarFront, BsDownload, BsFileText, BsImageFill, BsShield } from 'react-icons/bs';
import ImageGallery from '../../../components/elements/imageViewer/ImageGallery';
import { IoRefreshCircleOutline } from 'react-icons/io5';
import { UPLOADABLE_DOCUMENT_TYPES } from '../../../resources/enums';
import { IAction } from '../../../resources/interfaces';
import DocumentDownloadCard from '../../../components/cards/DocumentDownloadCard';
import VehicleAnalysisCard from './VehicleAnalysisCard';
import { Vehicle } from '../../../models/vehicle/Vehicle';
import useAnalysis from '../../../hooks/analysisHook';
import BackButton from '../../../components/BackButton';
import CustomerInfo from '../../../components/CustomerInfo';
import GTable from '../../../components/elements/GTable/GTable';
import VerificationDetailsDrawer from './VerificationDetailsDrawer';
import PopOnMe from '../../../components/elements/poponme';

const VerificationDetails = () => {
    const [isLoading, setIsLoading] = useState(true);
    const { user } = useUserAccount();
    const { getDashboardBase } = useRoute();
    const [verificationDetails, setVerificationDetails] = useState<VerifiedVehicle>();
    const { ModalComponent, updateModalStates, closeModal } = useModal();
    const { vehicleVerificationId } = useParams();
    const invalidateQueries = useQueryClient();
    const { getStatusIcon, getStatusColor } = useAnalysis();
    const navigate = useNavigate();
    const [isDetailsDrawerOpen, setIsDetailsDrawerOpen] = useState(false);
    const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
    const vehicleId = verificationDetails?.vehicles?.length === 1 ? verificationDetails?.vehicles[0].id : selectedVehicle?.id;
    const verificationId = verificationDetails?.id;
    const isFleetVehicle = verificationDetails?.vehicles && verificationDetails?.vehicles?.length > 1;

    // useEffect(() => {
    //     setVerificationDetails(new VerifiedVehicle(hardCodeVehicleVerificationData));
    // }, []);

    useEffect(() => {
        if (selectedVehicle) {
            setIsDetailsDrawerOpen(true);
        }
    }, [selectedVehicle]);

    const columns = useMemo(
        () => [
            {
                Header: 'M',
                columns: [
                    {
                        Header: 'Make',
                        accessor: 'make',
                        Cell: (row: any) => {
                            return <span>{row.cell.value ? row.cell.value : '-'}</span>;
                        }
                    },
                    {
                        Header: 'Model',
                        accessor: 'model',
                        Cell: (row: any) => {
                            return <span>{row.cell.value ? row.cell.value : '-'}</span>;
                        }
                    },
                    {
                        Header: 'Registration Number',
                        accessor: 'registrationNumber',
                        Cell: (row: any) => {
                            return <span>{row.cell.value || '-'}</span>;
                        }
                    },
                    {
                        Header: 'Driver Name',
                        accessor: 'driver.fullName',
                        Cell: (row: any) => {
                            return <span>{row.cell.value || '-'}</span>;
                        }
                    },
                    {
                        Header: 'Driver Phone',
                        accessor: 'driver.phoneNumber',
                        Cell: (row: any) => {
                            return <span>{getPhoneNumberFromShortCodeFormat(row.cell.value) || '-'}</span>;
                        }
                    },
                ]
            }
        ],
        []
    );

    useQuery({
        queryKey: ['getVerificationDetails', vehicleVerificationId],
        queryFn: () => getVerificationDetails(),
    });

    const getVerificationDetails = async () => {
        try {
            if (!vehicleVerificationId) return;
            const response: any = await getVerifiedVehicleById(vehicleVerificationId);
            setVerificationDetails(new VerifiedVehicle(response));
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    }

    const hadnleEditVehicleVerification = () => {
        updateModalStates({
            show: true,
            size: '3xl',
            children: (
                <CreateVerification
                    onProceed={() => {
                        closeModal()
                        onRefreshAnalysis()
                    }}
                    onClose={closeModal}
                    vehicleInfo={verificationDetails}
                    isEdit={true}
                    type='vehicle'
                />
            ),
            static: true,
        });
    }

    const handleExportPhotos = async () => {
        try {
            if (!verificationId || !vehicleId) return;
            const response: any = await exportVehicleVerificationPhotos(verificationId, vehicleId);
            if (response) {
                onRefreshAnalysis()
                setIsDetailsDrawerOpen(false)
                showToast.success(response.message ?? 'Photos exported successfully');
            }
        } catch (error: any) {
            console.log(error);
            showToast.error(error.description ?? 'Failed to export photos');
        }
    }

    const handleReloadPhotos = async () => {
        try {
            if (!verificationId || !vehicleId) return;
            const response: any = await reloadVehicleVerificationPhotos(verificationId, vehicleId);
            if (response) {
                showToast.success(response.message ?? 'Photos reloaded successfully');
                onRefreshAnalysis()
                setIsDetailsDrawerOpen(false)

            }
        } catch (error: any) {
            console.log(error);
            showToast.error(error.description ?? 'Failed to reload photos');
        }
    }

    const handleRequestPhotos = async (vehicleId?: string) => {
        try {
            if (!verificationId ) return;
            const response: any = await requestVehicleVerificationPhotos(verificationId, vehicleId);
            if (response) {
                onRefreshAnalysis()
                showToast.success(response.message ?? 'Photos requested successfully');
                setIsDetailsDrawerOpen(false)
            }
        } catch (error: any) {
            console.log(error);
            showToast.error(error.description ?? 'Failed to request photos');
        }
    }

    const getButtonActions = () => {
        const buttonActions: IAction[] = [
            {
                label: 'Edit',
                callback: () => hadnleEditVehicleVerification(),
                icon: <PencilIcon className="h-4 w-4" />
            },
            {
                label: 'Export photos',
                callback: () => handleExportPhotos(),
                icon: <BsDownload className="h-4 w-4" />
            },
            {
                label: 'Reload photos',
                callback: () => handleReloadPhotos(),
                icon: <IoRefreshCircleOutline className="h-4 w-4" />
            },
            {
                label: `${isFleetVehicle ? 'Request fleet photos' : 'Request photos'}`,
                callback: () => handleRequestPhotos(),
                icon: <BsFileText className="h-4 w-4" />
            },
        ];


        return buttonActions;
    }

    const getSingleVehicleButtonActions = () => {
        const buttonActions: IAction[] = [
            {
                label: 'Export photos',
                callback: () => handleExportPhotos(),
                icon: <BsDownload className="h-4 w-4" />
            },
            {
                label: 'Reload photos',
                callback: () => handleReloadPhotos(),
                icon: <IoRefreshCircleOutline className="h-4 w-4" />
            },
            {
                label: 'Request photos',
                callback: () =>vehicleId && handleRequestPhotos(vehicleId),
                icon: <BsFileText className="h-4 w-4" />
            },
        ];



        return buttonActions;
    }

    const inspectionReportPdf = verificationDetails?.vehicles?.[0]?.images?.filter((document) => document.docType === UPLOADABLE_DOCUMENT_TYPES.INSPECTION_REPORT)[0];

    const getFleetVehicleInfo = () => {
        return (
            <div className="bg-white rounded-lg shadow overflow-hidden mb-6 mt-6">
                {/* Action buttons */}
                <div className="flex py-4 justify-end">
                    {getSingleVehicleButtonActions()?.length > 0 && (
                        <ActionsPopOnMe actions={getSingleVehicleButtonActions()} showDropdown={false} />
                    )}
                </div>

                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                    <h2 className="text-lg font-medium text-gray-900 flex items-center">
                        <BsCarFront className="h-5 w-5 mr-2 text-gray-500" />
                        Vehicle Information
                    </h2>
                </div>

                <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                        <div className="col-span-1">
                            <div className="bg-gray-50 p-4 rounded-lg h-full">
                                <h3 className="text-md font-medium text-gray-900 mb-3">Vehicle Details</h3>
                                <div className="space-y-3 ">
                                    <VehicleDetailsItem name="Make" value={selectedVehicle?.make ?? '-'} />
                                    <VehicleDetailsItem name="Model" value={selectedVehicle?.model ?? '-'} />
                                    <VehicleDetailsItem name="Type" value={selectedVehicle?.type?.name ?? '-'} />
                                    <VehicleDetailsItem name="Color" value={selectedVehicle?.color ?? '-'} />
                                    <VehicleDetailsItem name="Chassis / VIN Number" value={selectedVehicle?.chassisNumber ?? '-'} />
                                    <VehicleDetailsItem name="CC" value={selectedVehicle?.cc ?? '-'} />
                                </div>
                            </div>
                        </div>

                        <div className="col-span-1">
                            <div className="bg-gray-50 p-4 rounded-lg h-full">
                                <h3 className="text-md font-medium text-gray-900 mb-3">Registration & Usage</h3>
                                <div className="space-y-3">
                                    <VehicleDetailsItem name="Manufactured Year" value={selectedVehicle?.manufacturedYear ?? '-'} />
                                    <VehicleDetailsItem name="Registration Year" value={selectedVehicle?.registrationYear ?? '-'} />
                                    <VehicleDetailsItem name="Vehicle Registration Number" value={selectedVehicle?.registrationNumber ?? '-'} />
                                    <VehicleDetailsItem name="Vehicle Usage" value={selectedVehicle?.usage?.title ?? '-'} />
                                    <VehicleDetailsItem name="Number of Passengers" value={selectedVehicle?.maxSeat ?? '-'} />
                                    <VehicleDetailsItem name="Vehicle Region" value={selectedVehicle?.region ?? '-'} />
                                </div>
                            </div>
                        </div>

                        <div className="bg-gray-50 rounded-lg h-full p-4">
                            <h3 className="text-md font-medium text-gray-900 mb-3">Owner & Value</h3>
                            <div className="grid grid-cols-2">
                                <VehicleDetailsItem name="Vehicle Owner" value={selectedVehicle?.ownerName || '-'} />
                                <VehicleDetailsItem name="Owner's Phone" value={selectedVehicle?.ownerPhoneNumber ? getPhoneNumberFromShortCodeFormat(selectedVehicle?.ownerPhoneNumber) : '-'} />
                            </div>
                            <div className="space-y-3 mt-3">
                                <VehicleDetailsItem name="Vehicle Main Driver" value={selectedVehicle?.driver?.fullName || '-'} />
                                <VehicleDetailsItem name="Vehicle Insurance Value" value={selectedVehicle?.insuredValue ? formatCurrencyAmount(selectedVehicle?.insuredValue, verificationDetails?.customer?.country) : '-'} />
                                <VehicleDetailsItem name="Nature of Interest in Vehicle" value={selectedVehicle?.natureOfInterest ?? '-'} />
                            </div>
                            <div className="space-y-3 mt-3">
                                <VehicleDetailsItem name="Accessories Installed" value={selectedVehicle?.installedAccessoriesDetails ?? '-'} />
                                <VehicleDetailsItem name="Accessories Value" value={selectedVehicle?.installedAccessoriesValue ? formatCurrencyAmount(selectedVehicle?.installedAccessoriesValue, verificationDetails?.customer?.country) : '-'} />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Vehicle Pictures Slideshow */}
                {selectedVehicle?.images && selectedVehicle?.images?.length > 0 && (
                    <div className="p-6 border-b border-gray-200">
                        <h3 className="text-md font-medium text-gray-900 mb-3 flex items-center">
                            <BsImageFill className="h-4 w-4 mr-2 text-gray-500" />
                            Vehicle Pictures
                        </h3>

                        <ImageGallery
                            images={selectedVehicle?.images || []}
                        />
                    </div>
                )}

                {selectedVehicle?.images && selectedVehicle?.images?.length > 0 && (
                    <div className="lg:col-span-1 mt-10">
                        <VehicleAnalysisCard vehicle={selectedVehicle} onRefresh={onRefreshAnalysis} />
                    </div>
                )}
            </div>
        )
    }

    const getVehicleInfo = () => {
        return (
            <div className="bg-white rounded-2xl shadow overflow-hidden mb-6 dark:bg-darkBg dark:border dark:border-darkBg-border">
                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 dark:bg-darkBg dark:border-darkBg-border">
                    <h2 className="text-lg font-medium text-gray-900 flex items-center dark:text-darkBg-mainText">
                        <BsCarFront className="h-5 w-5 mr-2 text-gray-500" />
                        Vehicle Information
                    </h2>
                </div>

                <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                        <div className="col-span-1">
                            <div className="bg-gray-50 p-4 rounded-lg h-full dark:bg-darkBg dark:border dark:border-darkBg-border">
                                <h3 className="text-md font-medium text-gray-900 mb-3 dark:text-darkBg-mainText">Vehicle Details</h3>
                                <div className="space-y-3">
                                    <VehicleDetailsItem name="Make" value={verificationDetails?.vehicles?.[0]?.make ?? '-'} />
                                    <VehicleDetailsItem name="Model" value={verificationDetails?.vehicles?.[0]?.model ?? '-'} />
                                    <VehicleDetailsItem name="Type" value={verificationDetails?.vehicles?.[0]?.type?.name ?? '-'} />
                                    <VehicleDetailsItem name="Color" value={verificationDetails?.vehicles?.[0]?.color ?? '-'} />
                                    <VehicleDetailsItem name="Chassis / VIN Number" value={verificationDetails?.vehicles?.[0]?.chassisNumber ?? '-'} />
                                    <VehicleDetailsItem name="CC" value={verificationDetails?.vehicles?.[0]?.cc ?? '-'} />
                                </div>
                            </div>
                        </div>
                        <div className="col-span-1">
                            <div className="bg-gray-50 p-4 rounded-lg h-full dark:bg-darkBg dark:border dark:border-darkBg-border">
                                <h3 className="text-md font-medium text-gray-900 mb-3 dark:text-darkBg-mainText">Registration & Usage</h3>
                                <div className="space-y-3">
                                    <VehicleDetailsItem name="Manufactured Year" value={verificationDetails?.vehicles?.[0]?.manufacturedYear ?? '-'} />
                                    <VehicleDetailsItem name="Registration Year" value={verificationDetails?.vehicles?.[0]?.registrationYear ?? '-'} />
                                    <VehicleDetailsItem name="Vehicle Registration Number" value={verificationDetails?.vehicles?.[0]?.registrationNumber ?? '-'} />
                                    <VehicleDetailsItem name="Vehicle Usage" value={verificationDetails?.vehicles?.[0]?.usage?.title ?? '-'} />
                                    <VehicleDetailsItem name="Number of Passengers" value={verificationDetails?.vehicles?.[0]?.maxSeat ?? '-'} />
                                    <VehicleDetailsItem name="Vehicle Region" value={verificationDetails?.vehicles?.[0]?.region ?? '-'} />
                                </div>
                            </div>
                        </div>

                    </div>
                    <div className="bg-gray-50 rounded-lg h-full p-4 dark:bg-darkBg dark:border dark:border-darkBg-border">
                        <h3 className="text-md font-medium text-gray-900 mb-3 dark:text-darkBg-mainText">Owner & Value</h3>
                        <div className=" grid grid-cols-3">

                            <div className="space-y-3">
                                <VehicleDetailsItem name="Vehicle Owner" value={verificationDetails?.vehicles?.[0]?.ownerName || '-'} />
                                <VehicleDetailsItem name="Owner's Phone" value={verificationDetails?.vehicles?.[0]?.ownerPhoneNumber ? getPhoneNumberFromShortCodeFormat(verificationDetails?.vehicles?.[0]?.ownerPhoneNumber) : '-'} />
                                <VehicleDetailsItem name="Vehicle Main Driver" value={verificationDetails?.vehicles?.[0]?.driver?.fullName || '-'} />
                            </div>
                            <div className="space-y-3">
                                <VehicleDetailsItem name="Vehicle Insurance Value" value={verificationDetails?.vehicles?.[0]?.insuredValue ? formatCurrencyAmount(verificationDetails?.vehicles?.[0]?.insuredValue, verificationDetails?.customer?.country) : '-'} />
                                <VehicleDetailsItem name="Nature of Interest in Vehicle" value={verificationDetails?.vehicles?.[0]?.natureOfInterest ?? '-'} />
                            </div>
                            <div className="space-y-3">
                                <VehicleDetailsItem name="Accessories Installed" value={verificationDetails?.vehicles?.[0]?.installedAccessoriesDetails ?? '-'} />
                                <VehicleDetailsItem name="Accessories Value" value={verificationDetails?.vehicles?.[0]?.installedAccessoriesValue ? formatCurrencyAmount(verificationDetails?.vehicles?.[0]?.installedAccessoriesValue, verificationDetails?.customer?.country) : '-'} />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Vehicle Pictures Slideshow */}
                {verificationDetails?.vehicles?.[0]?.images && verificationDetails?.vehicles?.[0]?.images?.length > 0 && (
                    <div className="p-6 border-b border-gray-200">
                        <h3 className="text-md font-medium text-gray-900 mb-3 flex items-center">
                            <BsImageFill className="h-4 w-4 mr-2 text-gray-500" />
                            Vehicle Pictures
                        </h3>

                        <ImageGallery
                            images={verificationDetails?.vehicles?.[0]?.images || []}
                        />
                    </div>
                )}
            </div>
        )
    }

    const getInspectionInfo = () => {
        return (
            <div className="bg-white rounded-2xl shadow overflow-hidden dark:bg-darkBg dark:border dark:border-darkBg-border">
                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 dark:bg-darkBg dark:border dark:border-darkBg-border">
                    <h2 className="text-lg font-medium text-gray-900 flex items-center dark:text-darkBg-mainText">
                        <BsShield className="h-5 w-5 mr-2 text-gray-500" />
                        Inspection Overview
                    </h2>
                </div>
                <div className="p-6">
                    <div className="grid grid-cols-1 gap-6">
                        <div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <VehicleDetailsItem name="Application Number" value={verificationDetails?.applicationNumber ?? '-'} />
                                <VehicleDetailsItem name="Insurer Name" value={verificationDetails?.insurer?.name ?? '-'} />
                                <VehicleDetailsItem name="Initiated By" value={verificationDetails?.staff?.fullName ?? '-'} />
                                <VehicleDetailsItem name="Initiated On" value={verificationDetails?.createdOn ? getFormattedDate({ date: verificationDetails?.createdOn ,  showTime: true }) : '-'} />
                                <VehicleDetailsItem name="Verified On" value={verificationDetails?.verifiedOn ? getFormattedDate({ date: verificationDetails?.verifiedOn , showTime: true }) : '-'} />
                                <VehicleDetailsItem name="Last Updated" value={verificationDetails?.updatedOn ? getFormattedDate({ date: verificationDetails?.updatedOn ,  showTime: true }) : '-'} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    const onRefreshAnalysis = () => {
        invalidateQueries.invalidateQueries({ queryKey: ['getVerificationDetails'] })
    }

    const getFleetVehiclesList = () => {
        if (!verificationDetails?.vehicles) return null;
        return (
            <div className="bg-white rounded-lg shadow overflow-hidden">

                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 dark:bg-darkBg dark:border-darkBg-border ">
                    <h2 className="text-lg font-medium text-gray-900 flex items-center dark:text-darkBg-mainText">
                        <BsCarFront className="h-5 w-5 mr-2 text-gray-500" />
                        Vehicle List
                    </h2>
                </div>

                <div className="overflow-x-auto">
                    <GTable
                        columns={columns}
                        data={verificationDetails?.vehicles || []}
                        classes="h-fit"
                        showSearch={false}
                        searchPlaceholder={'Search by name or phone number...'}
                        tableType={'vehicle info'}
                        stickyHeader
                        onRowClick={(row) => {
                            setSelectedVehicle(row)
                        }}
                    />
                </div>
            </div>
        )
    }

    const handleCloseVerificationDetailsDrawer = () => {
        setIsDetailsDrawerOpen(false);
        setSelectedVehicle(null);
        onRefreshAnalysis();
    }

    return (
        <div className={clsx('')}>
            {isLoading && (
                <div className="h-screen flex items-center justify-center w-full">
                    <Loader message="Loading, please wait..." brollyLoaderFixed />
                </div>
            )}

            {!isLoading && verificationDetails && (
                <div className="p-6">
                    {/* Inspection Header */}
                    <div className="flex  justify-between items-center mb-6">
                        <div>
                            <div className="flex items-center mb-2">
                                <div className="flex items-center gap-5">
                                    <BackButton onClick={() => navigate(`${getDashboardBase()}/vehicle-verification`)} />
                                    <div className='flex items-center justify-start gap-2'>
                                         <PopOnMe actions={getButtonActions()}>
                                                <EllipsisVerticalIcon className="h-6 w-6 md:hidden" />
                                        </PopOnMe>
                                        <h1 className="md:text-2xl text-lg whitespace-nowrap font-bold text-gray-900 dark:text-darkBg-mainText mr-3 ">
                                            VI-{verificationDetails?.applicationNumber ?? '-'}
                                        </h1>
                                    </div>
                                </div>
                                {/* <div className={`flex items-center px-3 py-1 rounded-full ${getStatusColor(verificationDetails?.verified)}`}>
                                    {getStatusIcon(verificationDetails?.verified)}
                                    <span className="ml-1 text-sm font-medium capitalize">{verificationDetails?.verified ?? '-'}</span>
                                </div> */}
                            </div>
                        </div>

                        {/* Action buttons */}
                        <div className="hidden md:flex py-4 justify-end">
                            {getButtonActions()?.length > 0 && (
                                <ActionsPopOnMe actions={getButtonActions()} showDropdown={false} />
                            )}
                        </div>
                    </div>

                    {/* Main Content - Two Column Layout */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                        {/* Left Column - 2/3 width */}
                        <div className="lg:col-span-2 space-y-6">

                            {/* Inspection Overview Section */}
                            {getInspectionInfo()}

                            {/* Vehicle Information Section */}
                            {verificationDetails?.vehicles && verificationDetails?.vehicles?.length > 1 ? (
                                <div className="">
                                    {getFleetVehiclesList()}
                                </div>
                            ) : (
                                <div className="">
                                    {getVehicleInfo()}

                                    {/* AI Analysis Section */}
                                    {verificationDetails?.vehicles?.[0]?.images && verificationDetails?.vehicles?.[0]?.images?.length > 0 && (
                                        <div className="lg:col-span-1 mt-10">
                                            <VehicleAnalysisCard vehicle={verificationDetails?.vehicles?.[0]} onRefresh={onRefreshAnalysis} />
                                        </div>
                                    )}
                                </div>
                            )}


                        </div>

                        {/* Right Column - 1/3 width */}
                        <div className="space-y-6">

                            {/* Customer Information Section */}
                            {verificationDetails?.customer && (
                                <CustomerInfo
                                    firstName={verificationDetails?.customer?.firstName}
                                    lastName={verificationDetails?.customer?.lastName}
                                    businessName={verificationDetails?.customer?.businessName}
                                    email={verificationDetails?.customer?.email}
                                    phoneNumber={verificationDetails?.customer?.phone}
                                    otherPhone={verificationDetails?.customer?.otherPhone}
                                    idNumber={verificationDetails?.customer?.idNumber}
                                    id={verificationDetails?.customer?.id}
                                    idVerified={verificationDetails?.customer?.idVerified}
                                    emailVerified={verificationDetails?.customer?.emailVerified}
                                    phoneVerified={verificationDetails.customer.phoneVerified || false}
                                    score={verificationDetails?.customer?.kycAnalysis?.score}
                                    risk={verificationDetails?.customer?.kycAnalysis?.risk}
                                    isClickable={true}
                                />
                            )}

                            {/* Inspection Timeline Section */}
                            <div className="bg-white rounded-2xl shadow overflow-hidden dark:bg-darkBg dark:border dark:border-darkBg-border">
                                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 dark:bg-darkBg dark:border dark:border-darkBg-border">
                                    <h2 className="text-lg font-medium text-gray-900 flex items-center dark:text-darkBg-mainText">
                                        <LuCalendarClock className="h-5 w-5 mr-2 text-gray-500" />
                                        Inspection Timeline
                                    </h2>
                                </div>
                                <div className="p-6 overflow-y-auto h-full md:max-h-[34rem]">
                                    {verificationDetails?.activityStreams && verificationDetails?.activityStreams?.length > 0 ? (
                                        <ActivityStepper
                                            activityStreams={verificationDetails.activityStreams}
                                            orientation="vertical"
                                            currentStep={0}
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center font-semibold">
                                            No activity
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Documents Section */}
                            {verificationDetails?.vehicles && verificationDetails?.vehicles?.length === 1 && (
                                <DocumentDownloadCard title="Documents" documents={inspectionReportPdf} />
                            )}
                        </div>
                    </div>
                </div>
            )}

            {!isLoading && !verificationDetails && (
                <div className="h-screen flex items-center justify-center w-full">
                    <span className="text-2xl font-bold text-gray-900">
                        Oops! Something went wrong
                    </span>
                </div>
            )}

            <VerificationDetailsDrawer vehicleInfo={getFleetVehicleInfo} isOpen={isDetailsDrawerOpen} onClose={handleCloseVerificationDetailsDrawer} />
            <ModalComponent />
        </div>
    )
}

type VehicleDetailsItemProps = {
    name: string;
    value?: string | number | JSX.Element;
    append?: React.ReactNode;
    prepend?: React.ReactNode;
};

const VehicleDetailsItem = ({ name, value, append, prepend }: VehicleDetailsItemProps) => {
    return (
        <div>
            <p className="text-sm font-medium text-gray-500">{name}</p>
            {prepend || append ? (
                <div className="flex items-center gap-1 mt-1">
                    {prepend}
                    <p className="text-sm text-gray-900 capitalize dark:text-darkBg-mainText">{value}</p>
                    {append}
                </div>
            ) : (
                <p className="mt-1 text-sm text-gray-900 capitalize dark:text-darkBg-mainText">{value}</p>
            )}
        </div>
    );
};

export default VerificationDetails